import { useState, useCallback, useEffect } from 'react';
import {
  Share2, Link2, Facebook, Twitter, MessageCircle,
  Mail, Smartphone, X, Check, Copy, Send, Plus, Search, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

const SHARE_TARGETS = [
  { id: 'copy', label: 'Copy Link', icon: Link2, color: 'text-foreground/80', bg: 'bg-muted/50' },
  { id: 'message', label: 'Send as Message', icon: Send, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'repost', label: 'Share as Post', icon: Plus, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'twitter', label: 'X / Twitter', icon: Twitter, color: 'text-sky-400', bg: 'bg-sky-500/20' },
  { id: 'messenger', label: 'Messenger', icon: MessageCircle, color: 'text-purple-400', bg: 'bg-purple-500/20' },
  { id: 'sms', label: 'SMS', icon: Smartphone, color: 'text-green-400', bg: 'bg-green-500/20' },
  { id: 'email', label: 'Email', icon: Mail, color: 'text-amber-400', bg: 'bg-amber-500/20' },
];

/**
 * Build a share URL that social platforms can crawl for OG tags.
 */
function getOgShareUrl(directUrl) {
  const origin = window.location.origin;
  const path = directUrl.replace(origin, '');
  
  const ogMap = [
    { regex: /^\/u\/(.+)$/, og: (m) => `/api/og/profile/${m[1]}` },
    { regex: /^\/track\/(.+)$/, og: (m) => `/api/og/track/${m[1]}` },
    { regex: /^\/post\/(.+)$/, og: (m) => `/api/og/post/${m[1]}` },
    { regex: /^\/video\/(.+)$/, og: (m) => `/api/og/video/${m[1]}` },
    { regex: /^\/live\/replay\/(.+)$/, og: (m) => `/api/og/replay/${m[1]}` },
    { regex: /^\/photo\/(.+)$/, og: (m) => `/api/og/photo/${m[1]}` },
    { regex: /^\/podcast\/(.+)$/, og: (m) => `/api/og/podcast/${m[1]}` },
    { regex: /^\/playlist\/(.+)$/, og: (m) => `/api/og/playlist/${m[1]}` },
  ];

  for (const { regex, og } of ogMap) {
    const match = path.match(regex);
    if (match) return `${origin}${og(match)}`;
  }
  return directUrl;
}

function getShareUrl(target, directUrl, ogUrl, title, description) {
  const socialUrl = encodeURIComponent(ogUrl);
  const encodedDirect = encodeURIComponent(directUrl);
  const encodedTitle = encodeURIComponent(title || '');
  const encodedDesc = encodeURIComponent(description || '');

  switch (target) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${socialUrl}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${socialUrl}&text=${encodedTitle}`;
    case 'messenger':
      return `https://www.facebook.com/dialog/send?link=${socialUrl}&app_id=0&redirect_uri=${encodedDirect}`;
    case 'sms':
      return `sms:?&body=${encodedTitle}%20${encodedDirect}`;
    case 'email':
      return `mailto:?subject=${encodedTitle}&body=${encodedDesc}%0A%0A${encodedDirect}`;
    default:
      return null;
  }
}

/**
 * DM Picker — picks a user to send a message to.
 */
function DmPicker({ url, title, onClose }) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [sending, setSending] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch recent conversations on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await fetch(`${API}/messages/conversations?tab=inbox&limit=10`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setConversations(data.conversations || data || []);
        }
      } catch {}
      setLoading(false);
    })();
  }, [isAuthenticated, getAuthHeaders]);

  // Search users
  useEffect(() => {
    if (!search.trim() || search.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/search?q=${encodeURIComponent(search)}&type=users&limit=8`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data.users || data.results || []);
        }
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [search, getAuthHeaders]);

  const sendToUser = async (userId, userName) => {
    setSending(userId);
    try {
      const messageText = title ? `${title}\n${url}` : url;
      const res = await fetch(`${API}/messages/conversations`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_user_id: userId,
          initial_message: messageText
        })
      });
      if (res.ok) {
        toast.success(`Sent to ${userName}`);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send');
      }
    } catch {
      toast.error('Failed to send message');
    }
    setSending(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Sign in to send messages
      </div>
    );
  }

  const displayList = search.trim() ? results : conversations;

  return (
    <div className="w-full">
      {/* Search input */}
      <div className="relative px-3 py-2">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-7 pr-3 py-1.5 rounded-lg text-xs bg-card/50 border border-border focus:border-border outline-none"
          data-testid="dm-search-input"
          autoFocus
        />
      </div>

      {/* User list */}
      <div className="max-h-48 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : displayList.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">
            {search.trim() ? 'No users found' : 'No recent conversations'}
          </p>
        ) : (
          displayList.map((item) => {
            const userId = item.other_user?.user_id || item.user_id;
            const name = item.other_user?.name || item.name || item.handle || 'User';
            const handle = item.other_user?.handle || item.handle || '';
            const avatar = item.other_user?.avatar_url || item.other_user?.picture || item.avatar_url || item.picture;
            return (
              <button
                key={userId || item.conversation_id}
                onClick={() => sendToUser(userId, name)}
                disabled={sending === userId}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/30 transition-colors text-left"
                data-testid={`dm-user-${userId}`}
              >
                <div className="w-7 h-7 rounded-full bg-muted overflow-hidden flex-shrink-0">
                  {avatar ? (
                    <img src={avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-muted-foreground">
                      {name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-foreground/90">{name}</p>
                  {handle && <p className="text-xs text-muted-foreground truncate">@{handle}</p>}
                </div>
                {sending === userId ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground flex-shrink-0" />
                ) : (
                  <Send className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * ShareButton — A reusable share button with dropdown sheet.
 */
export function ShareButton({ url, title, description, compact = false, disabled = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDmPicker, setShowDmPicker] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const fullUrl = url?.startsWith('http') ? url : `${window.location.origin}${url || ''}`;
  const ogUrl = getOgShareUrl(fullUrl);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || 'DMV Soundstage', text: description, url: ogUrl });
        return true;
      } catch (e) {
        if (e.name === 'AbortError') return true;
      }
    }
    return false;
  }, [ogUrl, title, description]);

  const handleClick = async () => {
    if (disabled) return;
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      const shared = await handleNativeShare();
      if (shared) return;
    }
    setOpen(true);
    setShowDmPicker(false);
  };

  const handleTarget = async (targetId) => {
    if (targetId === 'copy') {
      try {
        await navigator.clipboard.writeText(ogUrl);
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Failed to copy');
      }
      return;
    }

    if (targetId === 'message') {
      if (!isAuthenticated) {
        toast.info('Sign in to send messages');
        setOpen(false);
        navigate('/login');
        return;
      }
      setShowDmPicker(true);
      return;
    }

    if (targetId === 'repost') {
      if (!isAuthenticated) {
        toast.info('Sign in to create posts');
        setOpen(false);
        navigate('/login');
        return;
      }
      // Navigate to create post with the link pre-filled
      setOpen(false);
      navigate('/post/create', { state: { sharedLink: fullUrl, sharedTitle: title } });
      return;
    }

    const shareUrl = getShareUrl(targetId, fullUrl, ogUrl, title, description);
    if (shareUrl) {
      if (targetId === 'sms' || targetId === 'email') {
        window.location.href = shareUrl;
      } else {
        window.open(shareUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
      }
    }
    setOpen(false);
  };

  if (disabled) return null;

  return (
    <div className="relative">
      {compact ? (
        <button
          onClick={handleClick}
          className={`hover:opacity-70 transition-opacity ${className}`}
          data-testid="share-btn"
          title="Share"
        >
          <Share2 className="w-4 h-4" />
        </button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClick}
          className={`text-muted-foreground hover:text-foreground ${className}`}
          data-testid="share-btn"
        >
          <Share2 className="w-4 h-4 mr-1" />
          Share
        </Button>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-[9990]" onClick={() => { setOpen(false); setShowDmPicker(false); }} />
          <div
            className="absolute z-[9991] bottom-full mb-2 right-0 w-64 rounded-xl bg-card border border-border shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-150"
            data-testid="share-sheet"
          >
            <div className="px-3 py-2 border-b border-border flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {showDmPicker ? 'Send to' : 'Share'}
              </span>
              <div className="flex items-center gap-1">
                {showDmPicker && (
                  <button onClick={() => setShowDmPicker(false)} className="text-muted-foreground hover:text-foreground text-xs mr-1">
                    Back
                  </button>
                )}
                <button onClick={() => { setOpen(false); setShowDmPicker(false); }} className="text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {showDmPicker ? (
              <DmPicker 
                url={fullUrl}
                title={title}
                onClose={() => { setOpen(false); setShowDmPicker(false); }}
              />
            ) : (
              <div className="p-1.5">
                {SHARE_TARGETS.map(target => {
                  const Icon = target.icon;
                  return (
                    <button
                      key={target.id}
                      onClick={() => handleTarget(target.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm hover:bg-muted/30 transition-colors"
                      data-testid={`share-${target.id}`}
                    >
                      <div className={`w-7 h-7 rounded-lg ${target.bg} flex items-center justify-center`}>
                        {target.id === 'copy' && copied ? (
                          <Check className="w-3.5 h-3.5 text-green-400" />
                        ) : (
                          <Icon className={`w-3.5 h-3.5 ${target.color}`} />
                        )}
                      </div>
                      <span className="text-foreground/90">{target.id === 'copy' && copied ? 'Copied!' : target.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Inline share button for use in action bars (compact, icon-only).
 */
export function ShareIconButton({ url, title, description, className = '' }) {
  return <ShareButton url={url} title={title} description={description} compact className={className} />;
}
